import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import Attachments from '@buyside/components/presentation/IdeaForm/WriteupAndAttachments/Attachments/attachments';
import AttachmentPreview from '@buyside/components/presentation/IdeaForm/WriteupAndAttachments/Attachments/attachmentPreview';
import Writeup from '@buyside/components/presentation/IdeaForm/WriteupAndAttachments/Writeup/writeup';

import { IdeaFormInput } from '@buyside/util/researchSubmission';

import ChannelCheckSVG from '@buyside/svgs/channel_check.svg';
import FileTextSVG from '@buyside/svgs/file_text.svg';
import PaperStackSVG from '@buyside/svgs/paper_stack.svg';
import PenPaperSVG from '@buyside/svgs/pen_and_paper.svg';

export type AttachmentGroups = {
  [researchType: string]: {
    description: string;
    icon: string;
    attachmentPreviewIndices: { [index: number]: ReactNode };
  };
};

const WriteupAndAttachments: FC = () => {
  const {
    formState: { defaultValues },
    getValues
  } = useFormContext<IdeaFormInput>();

  const { append, fields, remove, update } = useFieldArray<IdeaFormInput>({
    name: 'attachments'
  });

  const attachmentGroups = useMemo(
    () =>
      fields.reduce<AttachmentGroups>(
        (acc, field, index) => {
          const researchType = getValues(`attachments.${index}.researchType`);

          acc[researchType] = {
            ...acc[researchType],
            attachmentPreviewIndices: {
              ...acc[researchType].attachmentPreviewIndices,
              [index]: (
                <AttachmentPreview key={field.id} index={index} remove={remove} update={update} />
              )
            }
          };

          return acc;
        },
        {
          Thesis: {
            description: 'Attach a PDF to use as your thesis.',
            icon: PenPaperSVG,
            attachmentPreviewIndices: {}
          },
          'Formatted Thesis': {
            description:
              'Upload another version of your thesis if it’s in a specially formatted document.',
            icon: PenPaperSVG,
            attachmentPreviewIndices: {}
          },
          'Financial Model': {
            description: 'Upload a file that supplements the information laid out in your thesis.',
            icon: FileTextSVG,
            attachmentPreviewIndices: {}
          },
          'Channel Check': {
            description: 'Provide additional information gleaned from primary research.',
            icon: ChannelCheckSVG,
            attachmentPreviewIndices: {}
          },
          'Secondary Research': {
            description: 'Analyst reports, media articles, legal filings, etc.',
            icon: PaperStackSVG,
            attachmentPreviewIndices: {}
          }
        }
      ),
    [fields, getValues, remove, update]
  );

  const { Thesis: thesisAttachmentGroup, ...remainingAttachmentGroups } = attachmentGroups;

  const [thesisEditorHidden, setThesisEditorHidden] = useState(
    Boolean(Object.keys(thesisAttachmentGroup.attachmentPreviewIndices).length) ||
      !defaultValues?.thesis
  );

  const handleRemoveFormattedThesisAttachment = useCallback(() => {
    const formattedThesisAttachmentIndex = Object.keys(
      remainingAttachmentGroups['Formatted Thesis'].attachmentPreviewIndices
    )[0];

    if (formattedThesisAttachmentIndex) remove(Number(formattedThesisAttachmentIndex));
  }, [remove, remainingAttachmentGroups]);

  const handleRemoveThesisAttachment = useCallback(() => {
    const thesisAttachmentIndex = Object.keys(thesisAttachmentGroup.attachmentPreviewIndices)[0];

    if (thesisAttachmentIndex) remove(Number(thesisAttachmentIndex));
  }, [remove, thesisAttachmentGroup]);

  return (
    <>
      <Writeup
        append={append}
        handleRemoveFormattedThesisAttachment={handleRemoveFormattedThesisAttachment}
        handleRemoveThesisAttachment={handleRemoveThesisAttachment}
        setThesisEditorHidden={setThesisEditorHidden}
        thesisAttachmentGroup={thesisAttachmentGroup}
        thesisEditorHidden={thesisEditorHidden}
      />
      <Attachments
        append={append}
        attachmentGroups={remainingAttachmentGroups}
        thesisEditorHidden={thesisEditorHidden}
      />
    </>
  );
};

export default WriteupAndAttachments;
